import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { BrandLogo } from '../brand/BrandWordmark';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';
import { aboutShort, configured } from '../../lib/platformContact';

export const Footer: React.FC = () => {
  const { language, t } = useLanguage();
  const { settings } = usePlatformSettings();
  const mission =
    (settings && aboutShort(settings, language)) ||
    t('about.defaultBody');
  const platformName = settings?.platformName || 'BeCoder';
  const salesEmail = settings?.salesEmail?.trim() || '';
  const supportEmail = settings?.supportEmail?.trim() || '';
  const footerContact = settings?.footerContactText?.trim() || '';
  const copyright =
    settings?.footerCopyrightText?.trim() ||
    `© ${new Date().getFullYear()} ${platformName}`;

  const social = settings
    ? [
        { href: settings.facebookUrl, label: 'Facebook' },
        { href: settings.instagramUrl, label: 'Instagram' },
        { href: settings.linkedinUrl, label: 'LinkedIn' },
        { href: settings.youtubeUrl, label: 'YouTube' },
      ].filter((s) => configured(s.href))
    : [];

  return (
    <footer className="border-t border-line dark:border-line-dark bg-paper dark:bg-ink mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid sm:grid-cols-12 gap-10 text-xs">
        <div className="sm:col-span-5 space-y-4">
          <BrandLogo size="sm" />
          <p className="text-fog leading-relaxed max-w-sm">{mission}</p>
        </div>
        <div className="sm:col-span-3 sm:col-start-7">
          <p className="font-display font-bold mb-3 text-ink dark:text-paper text-sm">
            {t('footer.platform')}
          </p>
          <ul className="space-y-2 text-fog">
            <li>
              <Link to="/contact" className="hover:text-brand-500 transition-colors">
                {t('nav.contactSales')}
              </Link>
            </li>
            <li>
              <Link to="/docs" className="hover:text-brand-500 transition-colors">
                {t('nav.docs')}
              </Link>
            </li>
            <li>
              <Link to="/status" className="hover:text-brand-500 transition-colors">
                {t('nav.status')}
              </Link>
            </li>
            <li>
              <Link to="/about" className="hover:text-brand-500 transition-colors">
                {t('nav.about')}
              </Link>
            </li>
          </ul>
        </div>
        <div className="sm:col-span-3">
          <p className="font-display font-bold mb-3 text-ink dark:text-paper text-sm">
            {t('footer.contact')}
          </p>
          <ul className="space-y-2 text-fog">
            <li>
              <Link to="/contact" className="hover:text-brand-500 transition-colors">
                {t('contact.title')}
              </Link>
            </li>
            {configured(footerContact) && <li className="whitespace-pre-wrap">{footerContact}</li>}
            {configured(salesEmail) && (
              <li>
                <a href={`mailto:${salesEmail}`} className="hover:text-brand-500 transition-colors">
                  {salesEmail}
                </a>
              </li>
            )}
            {configured(supportEmail) && (
              <li>
                <a
                  href={`mailto:${supportEmail}`}
                  className="hover:text-brand-500 transition-colors"
                >
                  {supportEmail}
                </a>
              </li>
            )}
            {social.map((s) => (
              <li key={s.label}>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-brand-500 transition-colors"
                >
                  {s.label}
                </a>
              </li>
            ))}
            <li>
              <Link to="/login" className="hover:text-brand-500 transition-colors">
                {t('nav.signIn')}
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-line dark:border-line-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between text-[10px] text-fog tracking-wide">
          <span>{copyright}</span>
          <span className="uppercase tracking-[0.14em]">{t('footer.privateDistribution')}</span>
        </div>
      </div>
    </footer>
  );
};
